import React, { useCallback, useState } from 'react';

import { AddCircle, Delete, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
	Box,
	Button,
	Checkbox,
	Collapse,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';

import { JSON_SCHEMA_FORMATS, JSON_SCHEMA_TYPES, JsonSchemaModel } from '../../Models/JsonSchemaModel';

const selectMenuProps = {
	sx: { zIndex: 1501 },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTypeLabel(schema: JsonSchemaModel): string {
	if (schema.enum) return 'Enum';
	switch (schema.type) {
		case JSON_SCHEMA_TYPES.STRING:
			switch (schema.format) {
				case JSON_SCHEMA_FORMATS.UUID:
					return 'Guid';
				case JSON_SCHEMA_FORMATS.DATE_TIME:
					return 'DateTime';
				case JSON_SCHEMA_FORMATS.DATE:
					return 'Date';
				case JSON_SCHEMA_FORMATS.TIME:
					return 'Time';
				default:
					return 'String';
			}
		case JSON_SCHEMA_TYPES.NUMBER:
			return 'Number';
		case JSON_SCHEMA_TYPES.INTEGER:
			return 'Integer';
		case JSON_SCHEMA_TYPES.BOOLEAN:
			return 'Boolean';
		case JSON_SCHEMA_TYPES.OBJECT:
			return 'Object';
		case JSON_SCHEMA_TYPES.ARRAY: {
			const itemLabel = getTypeLabel(getItemSchema(schema));
			return `Array<${itemLabel}>`;
		}
		case JSON_SCHEMA_TYPES.NULL:
			return 'Null';
		default:
			return String(schema.type);
	}
}

function getItemSchema(schema: JsonSchemaModel): JsonSchemaModel {
	return (Array.isArray(schema.items) ? undefined : schema.items) ?? { type: JSON_SCHEMA_TYPES.STRING };
}

function getStringPlaceholder(schema: JsonSchemaModel): string {
	switch (schema.format) {
		case JSON_SCHEMA_FORMATS.UUID:
			return '00000000-0000-0000-0000-000000000000';
		case JSON_SCHEMA_FORMATS.DATE_TIME:
			return '2026-01-01T00:00:00.000Z';
		case JSON_SCHEMA_FORMATS.DATE:
			return '2026-01-01';
		case JSON_SCHEMA_FORMATS.TIME:
			return '00:00:00';
		case JSON_SCHEMA_FORMATS.EMAIL:
			return 'user@example.com';
		case JSON_SCHEMA_FORMATS.URI:
			return 'https://example.com';
		default:
			return '';
	}
}

export function getDefaultValue(schema: JsonSchemaModel): unknown {
	if (schema.enum && schema.enum.length > 0) return schema.enum[0];
	switch (schema.type) {
		case JSON_SCHEMA_TYPES.OBJECT: {
			const obj: Record<string, unknown> = {};
			if (schema.properties) {
				for (const [k, v] of Object.entries(schema.properties)) {
					obj[k] = getDefaultValue(v);
				}
			}
			return obj;
		}
		case JSON_SCHEMA_TYPES.ARRAY:
			return [getDefaultValue(getItemSchema(schema))];
		case JSON_SCHEMA_TYPES.BOOLEAN:
			return false;
		case JSON_SCHEMA_TYPES.INTEGER:
		case JSON_SCHEMA_TYPES.NUMBER:
			return 0;
		case JSON_SCHEMA_TYPES.NULL:
			return null;
		default:
			return '';
	}
}

// ─── Primitive value input ────────────────────────────────────────────────────

type PrimitiveValueInputProps = {
	schema: JsonSchemaModel;
	value: unknown;
	onChange: (value: unknown) => void;
};

function PrimitiveValueInput({ schema, value, onChange }: PrimitiveValueInputProps) {
	if (schema.enum && schema.enum.length > 0) {
		const safeValue = schema.enum.includes(value as string | number) ? value : schema.enum[0];
		return (
			<Select value={safeValue} onChange={(e) => onChange(e.target.value)} size="small" fullWidth MenuProps={selectMenuProps}>
				{schema.enum.map((opt) => (
					<MenuItem key={String(opt)} value={opt}>
						{String(opt)}
					</MenuItem>
				))}
			</Select>
		);
	}

	switch (schema.type) {
		case JSON_SCHEMA_TYPES.STRING:
			return (
				<TextField
					value={String(value ?? '')}
					onChange={(e) => onChange(e.target.value)}
					size="small"
					fullWidth
					placeholder={getStringPlaceholder(schema)}
					multiline
					minRows={1}
				/>
			);

		case JSON_SCHEMA_TYPES.NUMBER:
			return (
				<TextField
					type="number"
					value={value !== null && value !== undefined ? String(value) : ''}
					onChange={(e) => {
						const n = parseFloat(e.target.value);
						onChange(isNaN(n) ? null : n);
					}}
					size="small"
					fullWidth
				/>
			);

		case JSON_SCHEMA_TYPES.INTEGER:
			return (
				<TextField
					type="number"
					value={value !== null && value !== undefined ? String(value) : ''}
					onChange={(e) => {
						const n = parseInt(e.target.value, 10);
						onChange(isNaN(n) ? null : n);
					}}
					size="small"
					fullWidth
					inputProps={{ step: 1 }}
				/>
			);

		case JSON_SCHEMA_TYPES.BOOLEAN:
			return <Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} size="small" />;

		case JSON_SCHEMA_TYPES.NULL:
			return (
				<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
					null
				</Typography>
			);

		default:
			return <TextField value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} size="small" fullWidth />;
	}
}

// ─── Array value editor ───────────────────────────────────────────────────────

type ArrayValueEditorProps = {
	itemSchema: JsonSchemaModel;
	value: unknown[];
	onChange: (value: unknown[]) => void;
	level: number;
};

function ArrayValueEditor({ itemSchema, value, onChange, level }: ArrayValueEditorProps) {
	const handleAddItem = () => onChange([...value, getDefaultValue(itemSchema)]);

	const handleRemoveItem = (index: number) => {
		const next = [...value];
		next.splice(index, 1);
		onChange(next);
	};

	const handleItemChange = (index: number, val: unknown) => {
		const next = [...value];
		next[index] = val;
		onChange(next);
	};

	const isObjectItem = itemSchema.type === JSON_SCHEMA_TYPES.OBJECT && !!itemSchema.properties;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
			{value.map((item, index) => (
				<Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
					<Box sx={{ flex: 1 }}>
						{isObjectItem ? (
							<Paper variant="outlined" sx={{ p: 1 }}>
								<Table size="small">
									<TableBody>
										{Object.entries(itemSchema.properties!).map(([childName, childSchema]) => (
											<PropertyRow
												key={childName}
												propName={childName}
												propSchema={childSchema}
												isRequired={itemSchema.required?.includes(childName) ?? false}
												value={((item as Record<string, unknown>) ?? {})[childName]}
												onChange={(val) => {
													const current = (item as Record<string, unknown>) ?? {};
													handleItemChange(index, { ...current, [childName]: val });
												}}
												level={level + 1}
											/>
										))}
									</TableBody>
								</Table>
							</Paper>
						) : (
							<PrimitiveValueInput schema={itemSchema} value={item} onChange={(val) => handleItemChange(index, val)} />
						)}
					</Box>
					<IconButton size="small" onClick={() => handleRemoveItem(index)} color="error" sx={{ mt: 0.5 }}>
						<Delete fontSize="small" />
					</IconButton>
				</Box>
			))}
			<Button size="small" startIcon={<AddCircle />} onClick={handleAddItem} sx={{ alignSelf: 'flex-start' }}>
				Add Item
			</Button>
		</Box>
	);
}

// ─── Single property row ──────────────────────────────────────────────────────

type PropertyRowProps = {
	propName: string;
	propSchema: JsonSchemaModel;
	isRequired: boolean;
	value: unknown;
	onChange: (value: unknown) => void;
	level: number;
};

function PropertyRow({ propName, propSchema, isRequired, value, onChange, level }: PropertyRowProps) {
	const [expanded, setExpanded] = useState(true);

	const isObjectWithProps = propSchema.type === JSON_SCHEMA_TYPES.OBJECT && !!propSchema.properties;
	const isArray = propSchema.type === JSON_SCHEMA_TYPES.ARRAY;

	const handleObjectPropertyChange = useCallback(
		(propKey: string, propValue: unknown) => {
			const current = (value as Record<string, unknown>) ?? {};
			onChange({ ...current, [propKey]: propValue });
		},
		[value, onChange],
	);

	return (
		<>
			<TableRow>
				<TableCell sx={{ paddingLeft: `${level * 24 + 12}px`, verticalAlign: 'top', width: '28%', pt: 1.25 }}>
					<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, pt: 0.5 }}>
						{isObjectWithProps && (
							<IconButton size="small" onClick={() => setExpanded((e) => !e)} sx={{ mt: -0.5 }}>
								{expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
							</IconButton>
						)}
						<Box>
							<Typography variant="body2" sx={{ fontWeight: isRequired ? 600 : 400 }}>
								{propName}
								{isRequired && (
									<Typography component="span" variant="body2" color="error.main">
										{' '}
										*
									</Typography>
								)}
							</Typography>
							<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.2 }}>
								{getTypeLabel(propSchema)}
							</Typography>
							{propSchema.description && (
								<Typography variant="caption" color="text.secondary" display="block">
									{propSchema.description}
								</Typography>
							)}
						</Box>
					</Box>
				</TableCell>

				<TableCell sx={{ verticalAlign: 'top', width: '72%', pt: 1.25 }}>
					{isObjectWithProps ? (
						// Leave it empty — value shown in nested collapse row below
						<Typography variant="body2" color="text.secondary" sx={{ py: 1, fontStyle: 'italic' }}>
							{expanded ? 'Expand to edit' : '…'}
						</Typography>
					) : isArray ? (
						<ArrayValueEditor itemSchema={getItemSchema(propSchema)} value={(value as unknown[]) ?? []} onChange={onChange} level={level} />
					) : (
						<PrimitiveValueInput schema={propSchema} value={value} onChange={onChange} />
					)}
				</TableCell>
			</TableRow>

			{isObjectWithProps && propSchema.properties && (
				<TableRow>
					<TableCell
						colSpan={2}
						sx={{
							pt: 0,
							pb: expanded ? 1 : 0,
							borderBottom: expanded ? undefined : 'none',
							backgroundColor: `rgba(0, 0, 0, ${0.02 * (level + 1)})`,
						}}
					>
						<Collapse in={expanded} timeout="auto" unmountOnExit>
							<Table size="small">
								<TableBody>
									{Object.entries(propSchema.properties).map(([childName, childSchema]) => (
										<PropertyRow
											key={childName}
											propName={childName}
											propSchema={childSchema}
											isRequired={propSchema.required?.includes(childName) ?? false}
											value={((value as Record<string, unknown>) ?? {})[childName]}
											onChange={(val) => handleObjectPropertyChange(childName, val)}
											level={level + 1}
										/>
									))}
								</TableBody>
							</Table>
						</Collapse>
					</TableCell>
				</TableRow>
			)}
		</>
	);
}

// ─── Main export ──────────────────────────────────────────────────────────────

export type JsonObjectInputProps = {
	schema: JsonSchemaModel;
	value: Record<string, unknown>;
	onChange: (value: Record<string, unknown>) => void;
};

export default function JsonObjectInput({ schema, value, onChange }: JsonObjectInputProps) {
	const handlePropertyChange = useCallback(
		(propName: string, propValue: unknown) => {
			onChange({ ...value, [propName]: propValue });
		},
		[value, onChange],
	);

	if (!schema.properties || Object.keys(schema.properties).length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				No properties defined in schema.
			</Typography>
		);
	}

	return (
		<TableContainer component={Paper} variant="outlined">
			<Table size="small" sx={{ tableLayout: 'fixed' }}>
				<TableHead>
					<TableRow>
						<TableCell sx={{ width: '28%' }}>Property</TableCell>
						<TableCell sx={{ width: '72%' }}>Value</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{Object.entries(schema.properties).map(([propName, propSchema]) => (
						<PropertyRow
							key={propName}
							propName={propName}
							propSchema={propSchema}
							isRequired={schema.required?.includes(propName) ?? false}
							value={value[propName]}
							onChange={(val) => handlePropertyChange(propName, val)}
							level={0}
						/>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
